/**
 * Ember avatars — the animal + scene pool and deterministic assignment.
 *
 * Supersedes the lucide symbol pool (2026-09-01 拍板, 整体替换): a presence
 * avatar is now a composed picture — one animal on one scene — stored as a
 * single `avatar_id` combo key `"<animal>:<scene>"`. The two halves live as
 * transparent-PNG animals and square scene paintings on Cloudinary
 * (`ember-avatars/animals/<key>`, `ember-avatars/scenes/<key>`), generated by
 * scripts/generate-avatar-assets.mjs in the image house style.
 *
 * One canonical list, shared by:
 *   - edge:  validates PATCH /api/me/presence {avatar_id}, assigns a default
 *            combo when an ember_users / ember_visitors row is born
 *            (actors.js) and inside finalize (presence.js ensurePresence);
 *   - web:   renders a combo as two stacked images (PresenceAvatar.tsx).
 *
 * The keys are the product contract; the painting behind a key may improve,
 * the key never changes (readers find "their person" by the picture). The
 * combo format is `animal:scene` — both halves are kebab-case slugs, the
 * colon can appear in neither.
 *
 * Pool curation rule (§0.9, carried over): non-photoreal, no human faces —
 * gentle animals in quiet places. A creature someone chooses to be, not a
 * portrait of who they are.
 */

export const AVATAR_ANIMAL_KEYS = Object.freeze([
  // woodland
  'red-fox', 'arctic-fox', 'rabbit', 'hedgehog', 'squirrel', 'chipmunk',
  'fawn', 'raccoon', 'badger', 'beaver',
  // wild & far away
  'brown-bear', 'polar-bear', 'panda', 'red-panda', 'koala', 'kangaroo',
  'sloth', 'elephant', 'giraffe', 'zebra', 'tiger', 'lion', 'wolf',
  // field & farm
  'horse', 'sheep', 'alpaca', 'capybara',
  // riverside & home
  'otter', 'seal', 'cat', 'dog', 'hamster', 'mouse',
  // birds
  'owl', 'penguin', 'duckling', 'robin', 'blue-jay', 'flamingo',
  'hummingbird', 'puffin',
  // sea
  'dolphin', 'whale', 'sea-turtle', 'koi', 'octopus',
  // small wonders
  'frog', 'butterfly', 'bee', 'snail',
]);

export const AVATAR_SCENE_KEYS = Object.freeze([
  // woods & groves
  'misty-forest', 'pine-forest', 'autumn-woods', 'birch-grove', 'bamboo-grove',
  'cherry-blossom', 'maple-lane', 'mossy-glen', 'fern-hollow', 'redwood-shade',
  // fresh water
  'lakeside-dawn', 'quiet-pond', 'river-bend', 'mountain-stream',
  'waterfall-mist', 'reed-marsh', 'lily-pond', 'tidal-pool', 'pebble-shore',
  'sea-cliff',
  // coast & sea
  'ocean-sunset', 'calm-harbor', 'lighthouse-coast', 'island-cove',
  'coral-lagoon', 'open-sea', 'dune-beach', 'driftwood-beach',
  // mountains & open land
  'alpine-meadow', 'snowy-peaks', 'rolling-hills', 'terraced-fields',
  'highland-mist', 'canyon-glow', 'desert-dusk', 'red-mesa', 'salt-flat',
  'prairie-wind',
  // fields in bloom
  'lavender-field', 'sunflower-field', 'wheat-field', 'tea-hills',
  'rice-paddies', 'tulip-field', 'poppy-meadow', 'clover-field',
  'wildflower-meadow', 'pumpkin-patch',
  // gardens
  'cottage-garden', 'rose-arbor', 'herb-garden', 'greenhouse-glass',
  'orchard-morning', 'vineyard-noon', 'zen-garden', 'courtyard-fountain',
  'window-box', 'balcony-plants',
  // sky & light
  'morning-sky', 'golden-hour', 'twilight-sky', 'starry-night',
  'crescent-moon', 'northern-lights', 'rainbow-clearing', 'summer-clouds',
  'gentle-rain', 'first-snow',
  // seasons turning
  'winter-forest', 'frozen-lake', 'spring-thaw', 'summer-lake', 'autumn-lane',
  'harvest-moon', 'foggy-morning', 'dew-grass',
  // hearth & shelter
  'warm-hearth', 'reading-nook', 'candle-light', 'kitchen-window',
  'attic-light', 'library-shelves', 'pottery-studio', 'music-room',
  // old towns & ways
  'old-town-lane', 'stone-bridge', 'village-rooftops', 'market-awnings',
  'train-window', 'country-road',
  // out under the sky
  'mountain-cabin', 'tent-under-stars', 'campfire-glow', 'treehouse',
  'windmill-field', 'paper-lanterns', 'hot-air-balloons', 'kite-sky',
  'quiet-snowfall', 'fireflies-dusk',
]);

const ANIMAL_SET = new Set(AVATAR_ANIMAL_KEYS);
const SCENE_SET = new Set(AVATAR_SCENE_KEYS);

/** `"red-fox:misty-forest"` → `{ animal, scene }`, or null when not a pool combo. */
export function parseAvatarKey(key) {
  const value = String(key || '');
  const colon = value.indexOf(':');
  if (colon <= 0) return null;
  const animal = value.slice(0, colon);
  const scene = value.slice(colon + 1);
  if (!ANIMAL_SET.has(animal) || !SCENE_SET.has(scene)) return null;
  return { animal, scene };
}

export function isAvatarComboKey(key) {
  return parseAvatarKey(key) !== null;
}

/** FNV-1a — the same stable hash the symbol pool used, kept for idempotency. */
function fnv1a(s) {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/**
 * Deterministic default combo for one actor id. Two independent hashes (the
 * scene half is salted) so animal and scene don't correlate across the pool;
 * re-running the assignment for the same actor can only ever compute the
 * same picture.
 */
export function assignedAvatarComboKey(actorId) {
  const s = String(actorId || '');
  const animal = AVATAR_ANIMAL_KEYS[fnv1a(s) % AVATAR_ANIMAL_KEYS.length];
  const scene = AVATAR_SCENE_KEYS[fnv1a(`${s}|scene`) % AVATAR_SCENE_KEYS.length];
  return `${animal}:${scene}`;
}

/**
 * Every stored avatar_id renders as SOME stable picture: a pool combo renders
 * itself; anything else (a retired lucide symbol key, an unknown value) maps
 * deterministically onto a combo, so a row migration 185 has not reached yet
 * can never draw an empty circle — and always draws the same one.
 */
export function normalizeAvatarKey(key) {
  return parseAvatarKey(key) || parseAvatarKey(assignedAvatarComboKey(key));
}

// ---------------------------------------------------------------------------
// Delivery URLs. The assets live on Cloudinary; the cloud name is public (it
// is in every delivered URL). Widths snap to a small ladder so the whole site
// shares a handful of CDN-cached renditions instead of minting one per pixel
// size (§2 image delivery contract).
// ---------------------------------------------------------------------------

const AVATAR_CDN_BASE = 'https://res.cloudinary.com/duxhtpggg/image/upload';
const AVATAR_WIDTHS = Object.freeze([48, 64, 96, 128, 160, 256]);

export function avatarRenditionWidth(cssPx) {
  const target = Math.max(1, Number(cssPx) || 0);
  for (const w of AVATAR_WIDTHS) if (w >= target) return w;
  return AVATAR_WIDTHS[AVATAR_WIDTHS.length - 1];
}

export function avatarAnimalSrc(animalKey, width) {
  return `${AVATAR_CDN_BASE}/f_auto,q_auto,w_${avatarRenditionWidth(width)}/ember-avatars/animals/${animalKey}`;
}

export function avatarSceneSrc(sceneKey, width) {
  // The leading crop keeps the center 84% of the painting: a generated wash
  // can carry a faint paper margin at its edges, and this guarantees the
  // margin never reaches the avatar disc regardless of how a batch came out.
  return `${AVATAR_CDN_BASE}/c_crop,g_center,ar_1:1,w_0.84/f_auto,q_auto,c_fill,w_${avatarRenditionWidth(width)}/ember-avatars/scenes/${sceneKey}`;
}
